import { useConfirm } from '@/components/Confirm';
import {
  type TaskBoardColumn,
  createTaskBoardColumn,
  deleteTaskBoardColumn,
  listTaskBoardColumns,
  reorderTaskBoardColumns,
  updateTaskBoardColumn,
} from '@/lib/api';
import { useEffect, useState } from 'react';

const STATUS_OPTIONS = [
  'pending',
  'confirmed',
  'confirmed_deposit',
  'paid',
  'checked_in',
  'checked_out',
  'cancelled',
  'no_show',
] as const;

const PRESET_FILTERS: Array<{ label: string; filter: unknown }> = [
  {
    label: 'Arriving today',
    filter: {
      all: [
        { kind: 'status_in', values: ['confirmed', 'confirmed_deposit', 'paid'] },
        { kind: 'date_range', field: 'check_in', preset: 'today' },
      ],
    },
  },
  { label: 'In care', filter: { all: [{ kind: 'status_in', values: ['checked_in'] }] } },
  {
    label: 'Departing today',
    filter: {
      all: [
        { kind: 'status_in', values: ['checked_in'] },
        { kind: 'date_range', field: 'checkout', preset: 'today' },
      ],
    },
  },
  {
    label: 'Recently departed',
    filter: {
      all: [
        { kind: 'status_in', values: ['checked_out'] },
        { kind: 'date_range', field: 'checkout', preset: 'today' },
      ],
    },
  },
];

export function TaskBoardSettingsPage() {
  const [columns, setColumns] = useState<TaskBoardColumn[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [newStatuses, setNewStatuses] = useState<Set<string>>(
    new Set(['confirmed', 'confirmed_deposit', 'paid']),
  );
  const { confirm, ConfirmDialog } = useConfirm();

  async function refresh() {
    setLoading(true);
    try {
      const fresh = await listTaskBoardColumns();
      setColumns(fresh);
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  async function handleAdd(e: React.FormEvent) {
    e.preventDefault();
    if (!newName.trim() || newStatuses.size === 0) return;
    try {
      await createTaskBoardColumn({
        name: newName.trim(),
        filter: { all: [{ kind: 'status_in', values: Array.from(newStatuses) }] },
        position: columns.length,
      });
      setNewName('');
      await refresh();
    } catch (err) {
      setError((err as Error).message);
    }
  }

  async function handlePreset(preset: { label: string; filter: unknown }) {
    try {
      await createTaskBoardColumn({
        name: preset.label,
        filter: preset.filter,
        position: columns.length,
      });
      await refresh();
    } catch (err) {
      setError((err as Error).message);
    }
  }

  async function moveColumn(index: number, dir: -1 | 1) {
    const next = index + dir;
    if (next < 0 || next >= columns.length) return;
    const reordered = [...columns];
    const [moved] = reordered.splice(index, 1);
    if (!moved) return;
    reordered.splice(next, 0, moved);
    const items = reordered.map((c, i) => ({ id: c.id, position: i }));
    await reorderTaskBoardColumns(items);
    void refresh();
  }

  return (
    <div className="space-y-4">
      <ConfirmDialog />
      <div>
        <h1 className="font-heading text-2xl font-medium tracking-tight">Task board layout</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Tenant-defined columns that drive the staff /task-board page. Each column has a filter
          (status + date range + tags) that selects which bookings appear.
        </p>
      </div>

      {error && (
        <div className="mb-4 rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      )}

      <form onSubmit={handleAdd} className="mb-6 rounded-xl border border-border bg-surface p-4">
        <h2 className="mb-3 text-sm font-medium">Add a column</h2>
        <div className="flex flex-col gap-3">
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Column name"
            className="h-9 rounded-md border border-border bg-surface px-2 text-sm"
          />
          <div>
            <div className="mb-2 text-xs text-muted-foreground">
              Match bookings in any of these statuses:
            </div>
            <div className="flex flex-wrap gap-2">
              {STATUS_OPTIONS.map((s) => {
                const on = newStatuses.has(s);
                return (
                  <button
                    type="button"
                    key={s}
                    onClick={() => {
                      const next = new Set(newStatuses);
                      if (on) next.delete(s);
                      else next.add(s);
                      setNewStatuses(next);
                    }}
                    className={
                      on
                        ? 'rounded-full bg-primary px-3 py-1 text-xs font-medium text-primary-foreground'
                        : 'rounded-full border border-border bg-surface px-3 py-1 text-xs text-muted-foreground hover:text-foreground'
                    }
                  >
                    {s}
                  </button>
                );
              })}
            </div>
          </div>
          <button
            type="submit"
            className="self-start h-9 rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            Add column
          </button>
        </div>
      </form>

      <div className="mb-6 rounded-xl border border-border bg-surface p-4">
        <h2 className="mb-3 text-sm font-medium">Quick add — defaults</h2>
        <div className="flex flex-wrap gap-2">
          {PRESET_FILTERS.map((p) => (
            <button
              key={p.label}
              type="button"
              onClick={() => void handlePreset(p)}
              className="rounded-full border border-border bg-surface px-3 py-1 text-xs text-muted-foreground hover:text-foreground"
            >
              + {p.label}
            </button>
          ))}
        </div>
      </div>

      {loading && <div className="text-sm text-muted-foreground">Loading…</div>}

      {!loading && (
        <div className="space-y-2">
          {columns.map((col, i) => (
            <div
              key={col.id}
              className="flex items-center justify-between rounded-md border border-border bg-surface px-3 py-2"
            >
              <div className="flex items-center gap-3">
                <div className="flex flex-col">
                  <button
                    type="button"
                    onClick={() => void moveColumn(i, -1)}
                    disabled={i === 0}
                    className="text-xs text-muted-foreground disabled:opacity-30"
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    onClick={() => void moveColumn(i, 1)}
                    disabled={i === columns.length - 1}
                    className="text-xs text-muted-foreground disabled:opacity-30"
                  >
                    ↓
                  </button>
                </div>
                <span className="text-sm">{col.name}</span>
                <code className="text-xs text-muted-foreground">{col.filterJson}</code>
              </div>
              <div className="flex gap-2 text-xs">
                <button
                  type="button"
                  onClick={async () => {
                    const next = prompt('New name', col.name);
                    if (next?.trim()) {
                      await updateTaskBoardColumn(col.id, { name: next.trim() });
                      void refresh();
                    }
                  }}
                  className="text-muted-foreground hover:text-foreground"
                >
                  Rename
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    const yes = await confirm({
                      title: `Delete "${col.name}"?`,
                      danger: true,
                      confirmLabel: 'Delete',
                    });
                    if (!yes) return;
                    await deleteTaskBoardColumn(col.id);
                    void refresh();
                  }}
                  className="text-destructive hover:underline"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
